import {
  CheckOutlined,
  CloseCircleOutlined,
  EditOutlined,
  HomeOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App,
  Button,
  Card,
  Checkbox,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { TableProps } from "antd";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { speakerPhones, speechLabel } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  CongregationArchiveRow,
  ListResponse,
  LocalEventListResponse,
  NotificationsResponse,
  WeekSpeakerRow,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import type { ExportableColumn } from "../components/DataTable";
import { HostDialog } from "../components/HostDialog";
import { SpeakerName, speakerNameText } from "../components/SpeakerName";

interface EventFormValues {
  event_type: number;
  additional_notes: string;
  day: number | null;
  time: string;
}

/**
 * Sidebar entry "local_assignations" → /local_assignation/assignations.
 *
 * The congregation's own calendar: what happens each week, who is hosting the
 * visiting speaker, and which of the congregation's own speakers are asked to
 * keep the week free.
 *
 * A week the zone has claimed is marked and cannot be edited here — the
 * circumscription owns it, and the server refuses.
 */
export function LocalAssignationPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<EventFormValues>();

  const [editing, setEditing] = useState<CongregationArchiveRow | null>(null);
  const [hostOf, setHostOf] = useState<CongregationArchiveRow | null>(null);
  const [speakersOf, setSpeakersOf] = useState<CongregationArchiveRow | null>(null);

  const events = useQuery({
    queryKey: ["local_assignation", "events"],
    queryFn: () => apiFetch<LocalEventListResponse>("/local_assignation/events"),
  });

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["local_assignation"] });
    void queryClient.invalidateQueries({ queryKey: ["archive"] });
  }, [queryClient]);

  const saveEvent = useMutation({
    mutationFn: ({ week, values }: { week: string; values: EventFormValues }) =>
      apiFetch<NotificationsResponse>(`/local_assignation/events/${week}`, {
        method: "PUT",
        body: values,
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      setEditing(null);
      refresh();
    },
    onError: showError,
  });

  const cancelEvent = useMutation({
    mutationFn: (week: string) =>
      apiFetch<NotificationsResponse>(`/local_assignation/events/${week}`, { method: "DELETE" }),
    onSuccess: (data) => {
      notify(data.notifications);
      refresh();
    },
    onError: showError,
  });

  const busy = saveEvent.isPending || cancelEvent.isPending;

  const weekDays = useMemo(
    () =>
      (t("date.day_names", { returnObjects: true }) as string[]).map((label, index) => ({
        value: index,
        label,
      })),
    [t],
  );

  // Who is putting the speaker up — or, until somebody is named, why not yet.
  // Read twice: once for the cell, once for the export.
  const hostLabel = useCallback(
    (row: CongregationArchiveRow): string | null =>
      row.hospitality_host_name ??
      (row.hospitality_status === "not_provided"
        ? t("local_assignation.hospitality_not_provided")
        : row.hospitality_status
          ? t("local_assignation.hospitality_not_established")
          : null),
    [t],
  );

  const columns = useMemo<ExportableColumn<CongregationArchiveRow>[]>(
    () => [
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        dataIndex: "week_label",
        width: 190,
        fixed: "left",
        // The tag goes under the date instead of beside it: side by side the
        // two share the column's width and the date is what breaks.
        render: (value: string, row) => (
          <Space orientation="vertical" size={0} align="start">
            <span style={{ whiteSpace: "nowrap" }}>{value}</span>
            {row.modified && (
              <Tag color="green" style={{ marginInlineEnd: 0 }}>
                {t("icon.modified")}
              </Tag>
            )}
          </Space>
        ),
        exportValue: (row) =>
          [row.week_label, row.modified ? t("icon.modified") : null]
            .filter(Boolean)
            .join(" · "),
      },
      {
        title: t("activerecord.attributes.local_assignation.week_day"),
        dataIndex: "week_day_label",
        width: 110,
      },
      {
        title: t("activerecord.attributes.local_assignation.day_time"),
        dataIndex: "day_time",
        width: 80,
      },
      {
        title: t("activerecord.attributes.local_assignation.event_type"),
        dataIndex: "event_type_label",
        width: 190,
        // The tag says what kind of event this is, so it belongs to the event
        // and not to the week it falls in — it stands in front of the name of
        // the event the way an adjective does. No ellipsis on the column: it
        // would cut the tag and the name together into one truncated line.
        render: (value: string | null, row) =>
          row.is_circumscription_event ? (
            <Space size={4} wrap>
              <Tooltip title={t("speaker_assignation.circumscription_requested")}>
                <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                  {t("activerecord.models.circumscription")}
                </Tag>
              </Tooltip>
              <span>{value}</span>
            </Space>
          ) : (
            value
          ),
        exportValue: (row) =>
          [
            row.is_circumscription_event ? t("activerecord.models.circumscription") : null,
            row.event_type_label,
          ]
            .filter(Boolean)
            .join(" · "),
      },
      {
        title: t("activerecord.attributes.local_assignation.additional_notes"),
        dataIndex: "additional_notes",
        width: 200,
        ellipsis: true,
      },
      {
        // The number of the outline and the title of the talk in one cell, see
        // speechLabel — on its own the number said little and took the width
        // the titles needed.
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "speech_title",
        width: 260,
        ellipsis: true,
        render: (_value: string | null, row: CongregationArchiveRow) =>
          speechLabel(row.speech_schema, row.speech_title, t),
        exportValue: (row: CongregationArchiveRow) =>
          speechLabel(row.speech_schema, row.speech_title, t),
      },
      {
        title: t("activerecord.attributes.local_assignation.speaker_id"),
        dataIndex: "speaker_name",
        width: 170,
        ellipsis: true,
        render: (_value: string | null, row) => (
          <SpeakerName
            name={row.speaker_name}
            contactMe={row.speaker_contact_me}
            hint={t("speaker.contact_me")}
          />
        ),
        exportValue: (row) => speakerNameText(row.speaker_name, row.speaker_contact_me, t),
      },
      {
        title: t("activerecord.models.congregation"),
        dataIndex: "speaker_congregation",
        width: 160,
        ellipsis: true,
      },
      {
        // Both numbers of the account, one under the other: which of the two a
        // speaker keeps is up to them, and beside each other they would need
        // the width of two columns to stay readable.
        title: t("activerecord.attributes.user.phone"),
        key: "speaker_phone",
        width: 150,
        render: (_value: unknown, row) => (
          <Space orientation="vertical" size={0} align="start">
            {speakerPhones(row).map((number) => (
              <span key={number} style={{ whiteSpace: "nowrap" }}>
                {number}
              </span>
            ))}
          </Space>
        ),
        // No dataIndex to fall back on: the cell is built from two fields.
        exportValue: (row) => speakerPhones(row).join(" · ") || null,
      },
      {
        title: t("activerecord.attributes.user.private_email"),
        dataIndex: "speaker_email",
        width: 200,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.local_assignation.speaker_host_name"),
        dataIndex: "hospitality_host_name",
        width: 160,
        ellipsis: true,
        render: (_value: string | null, row) => hostLabel(row),
        exportValue: (row) => hostLabel(row),
      },
      {
        key: "actions",
        fixed: "right",
        width: 150,
        render: (_value, row) => (
          <Space size={4}>
            <Tooltip title={t("form.update")}>
              <Button
                size="small"
                icon={<EditOutlined />}
                // The zone owns its weeks; the server refuses an edit here.
                disabled={busy || row.is_circumscription_event}
                onClick={() => {
                  setEditing(row);
                  form.resetFields();
                  form.setFieldsValue({
                    // Only a week without an event still asks for a type.
                    event_type: row.has_event ? undefined : (row.event_type ?? undefined),
                    additional_notes: row.additional_notes ?? "",
                    // The week already has a day; it belongs in the field, or
                    // the list offers a choice that looks like none was made.
                    day: row.week_day ?? null,
                    time: row.day_time ?? "",
                  } as EventFormValues);
                }}
              />
            </Tooltip>

            <Tooltip title={t("zone_assignation.speaker_requested")}>
              <Button
                size="small"
                icon={<TeamOutlined />}
                disabled={busy}
                onClick={() => setSpeakersOf(row)}
              />
            </Tooltip>

            {/* Only a week with a visiting speaker can have a host. */}
            {row.speaker_name && (
              <Tooltip title={t("local_assignation.host_assign")}>
                <Button
                  size="small"
                  icon={<HomeOutlined />}
                  disabled={busy}
                  onClick={() => setHostOf(row)}
                />
              </Tooltip>
            )}

            {row.has_event && (
              <Tooltip title={t("local_assignation.cancel")}>
                <Button
                  size="small"
                  danger
                  icon={<CloseCircleOutlined />}
                  disabled={busy || row.is_circumscription_event}
                  onClick={() =>
                    modal.confirm({
                      title: t("dialog.confirmation_title"),
                      content: (
                        <>
                          <Typography.Paragraph>{row.week_label}</Typography.Paragraph>
                          {t("local_assignation.confirm_cancel_local_event")}
                        </>
                      ),
                      okText: t("dialog.answer_yes"),
                      okButtonProps: { danger: true },
                      cancelText: t("dialog.answer_no"),
                      onOk: () => cancelEvent.mutate(row.week_begin),
                    })
                  }
                />
              </Tooltip>
            )}
          </Space>
        ),
      },
    ],
    [t, busy, form, cancelEvent, modal, hostLabel],
  );

  return (
    <Card title={t("navigation.local_assignations")}>
      <DataTable<CongregationArchiveRow>
        columns={columns}
        rows={events.data?.data}
        exportAs={t("navigation.local_assignations")}
        loading={events.isPending || busy}
        error={events.error}
        rowKey="week_begin"
        rowClassName={(row) => (row.has_event ? "" : "local-empty-week")}
      />

      <style>{`.local-empty-week { opacity: 0.6; }`}</style>

      <Modal
        open={editing !== null}
        title={editing?.week_label}
        onCancel={() => setEditing(null)}
        okText={t("form.update")}
        okButtonProps={{ loading: saveEvent.isPending }}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form<EventFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => saveEvent.mutate({ week: editing!.week_begin, values })}
        >
          {/* Only while the event is being created. The server settles the
              type then and keeps it afterwards (local_assignation_edit
              reassigns event_type from the record it found), so on an existing
              event the field has nothing left to offer — changing it means
              cancelling the event and entering it again. */}
          {!editing?.has_event && (
            <Form.Item
              name="event_type"
              label={t("activerecord.attributes.local_assignation.event_type")}
            >
              <Select options={events.data?.event_types ?? []} style={{ width: 280 }} />
            </Form.Item>
          )}

          {/* Day and time override the congregation's default for this week
              only — the defaults come from /congregation/congregation_times. */}
          <Form.Item
            name="day"
            label={t("activerecord.attributes.local_assignation.week_day")}
          >
            <Select options={weekDays} allowClear style={{ width: 200 }} />
          </Form.Item>

          <Form.Item
            name="time"
            label={t("activerecord.attributes.local_assignation.day_time")}
            rules={[
              {
                pattern: /^([01]\d|2[0-3]):[0-5]\d$/,
                message: t(
                  "activerecord.errors.models.congregation_meeting_time.attributes.day_time.invalid_format",
                ),
              },
            ]}
          >
            <Input placeholder="hh:mm" style={{ width: 140 }} />
          </Form.Item>

          <Form.Item
            name="additional_notes"
            label={t("activerecord.attributes.local_assignation.additional_notes")}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {hostOf && (
        <HostDialog week={hostOf} onClose={() => setHostOf(null)} onChanged={refresh} />
      )}

      {speakersOf && (
        <WeekSpeakersDrawer event={speakersOf} onClose={() => setSpeakersOf(null)} onChanged={refresh} />
      )}
    </Card>
  );
}

/**
 * The congregation's own speakers in one week. Asking a speaker keeps them at
 * home for the congregation's event; the columns on the right show where a
 * speaker is going instead, so the coordinator can see who is actually free.
 */
function WeekSpeakersDrawer({
  event,
  onClose,
  onChanged,
}: {
  event: CongregationArchiveRow;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => ["local_assignation", "week_speakers", event.week_begin],
    [event.week_begin],
  );

  const speakers = useQuery({
    queryKey,
    queryFn: () =>
      apiFetch<ListResponse<WeekSpeakerRow>>(
        `/local_assignation/events/${event.week_begin}/speakers`,
      ),
  });

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const reload = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey });
    onChanged();
  }, [queryClient, queryKey, onChanged]);

  const request = useMutation({
    mutationFn: ({ row, requested }: { row: WeekSpeakerRow; requested: boolean }) =>
      apiFetch<NotificationsResponse>(
        `/local_assignation/events/${event.week_begin}/speakers/${row.id}`,
        { method: "PUT", body: { speaker_requested: requested } },
      ),
    onSuccess: (data) => {
      notify(data.notifications);
      reload();
    },
    onError: showError,
  });

  /**
   * The whole congregation at once, which is what a week the congregation keeps
   * to itself usually means. The old screen carried this as the single button
   * of the sub-grid's toolbar; without it every speaker has to be ticked by
   * hand, and each tick is a request of its own.
   */
  const requestAll = useMutation({
    mutationFn: () =>
      apiFetch<NotificationsResponse>(`/local_assignation/events/${event.week_begin}/speakers`, {
        method: "PUT",
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      reload();
    },
    onError: showError,
  });

  const busy = request.isPending || requestAll.isPending;

  const columns = useMemo<TableProps<WeekSpeakerRow>["columns"]>(
    () => [
      {
        title: t("zone_assignation.speaker_requested"),
        dataIndex: "speaker_requested",
        width: 150,
        render: (value: boolean, row) => (
          <Checkbox
            checked={value}
            disabled={busy}
            onChange={(change) => request.mutate({ row, requested: change.target.checked })}
          />
        ),
      },
      {
        title: t("activerecord.attributes.speaker.first_name"),
        dataIndex: "denomination",
        width: 200,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.local_assignation.congregation_id"),
        dataIndex: "assigned_congregation",
        width: 180,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "assigned_speech_schema",
        width: 90,
        align: "right",
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "assigned_speech_title",
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.user.phone"),
        dataIndex: "phone",
        width: 140,
      },
      {
        title: t("activerecord.attributes.local_assignation.additional_notes"),
        dataIndex: "additional_notes",
        width: 180,
        ellipsis: true,
      },
    ],
    [t, busy, request],
  );

  return (
    <Drawer
      open
      onClose={onClose}
      size={1000}
      destroyOnHidden
      title={[event.week_label, event.event_type_label].filter(Boolean).join(" · ")}
      extra={
        <Button
          type="primary"
          icon={<CheckOutlined />}
          loading={requestAll.isPending}
          disabled={busy}
          onClick={() => requestAll.mutate()}
        >
          {t("local_assignation.speaker_attendance")}
        </Button>
      }
    >
      <DataTable<WeekSpeakerRow>
        columns={columns}
        rows={speakers.data?.data}
        exportAs={[event.week_label, t("zone_assignation.speaker_requested")]
          .filter(Boolean)
          .join(" · ")}
        loading={speakers.isPending || busy}
        error={speakers.error}
      />
    </Drawer>
  );
}
